import type { MouseEvent } from "react";
import IconButton, { type IconButtonProps } from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { useBusyClick } from "../../hooks/useBusyClick";
import {
  type ConfirmDeleteProp,
  useConfirmableClick,
} from "../../hooks/useConfirmableClick";

export type AppIconButtonProps = IconButtonProps & {
  loading?: boolean;
  busyIgnore?: boolean;
  /** Tooltip while idle; while busy shows common.working. */
  title?: string;
  /** When set, shows a delete confirmation dialog before running onClick. */
  confirmDelete?: ConfirmDeleteProp;
  /** react-router Link target when `component={RouterLink}`. */
  to?: string;
};

/** IconButton with soft fill (Win11) and the same auto-busy behavior as AppButton. */
export default function AppIconButton({
  loading = false,
  busyIgnore = false,
  confirmDelete,
  disabled,
  onClick,
  children,
  title,
  size = "small",
  sx,
  color = "default",
  ...rest
}: AppIconButtonProps) {
  const { t } = useTranslation();
  const confirmedOnClick = useConfirmableClick(onClick, confirmDelete);
  const { busy, onClick: busyOnClick } = useBusyClick<MouseEvent<HTMLButtonElement>>(
    confirmedOnClick,
    {
      loading,
      disabled: !!disabled,
      busyIgnore,
    }
  );
  const showBusy = busy && !busyIgnore;
  const spinnerSize = size === "large" ? 22 : size === "medium" ? 18 : 14;

  const button = (
    <IconButton
      size={size}
      color={color}
      disabled={disabled || showBusy}
      onClick={busyOnClick}
      aria-busy={showBusy || undefined}
      aria-label={showBusy ? t("common.working") : title}
      sx={[
        {
          borderRadius: 2.5,
          bgcolor: "background.tonal",
          "&:hover": { bgcolor: "background.tonalHover" },
          "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    >
      {showBusy ? <CircularProgress color="inherit" size={spinnerSize} /> : children}
    </IconButton>
  );

  const tip = showBusy ? t("common.working") : title;
  if (!tip) {
    return button;
  }
  return <Tooltip title={tip}>{button}</Tooltip>;
}
